/**
 * 通用节点作业页（F01）—— 现场操作员在某一履约节点处理业务的统一入口。
 *
 * 设计原则：
 * - 子步骤交互完全对齐后台 NodeSubstepPanel
 * - 节点级附件（node_doc_rules 中 substepCode 为空的规则）单独展示并随 advance.files 提交
 * - 实际发生时间由后端/服务自动记录，前端不再默认展示时间窗口
 *
 * 对接真实后端：
 * - GET /order/{id}/advance-permission  推进前权限（门控单一事实来源）
 * - GET /order/{id}/node-action-hint    节点操作提示（NodeSubstepPanel 内部也用）
 * - PUT /order/{id}/advance             节点推进（携带 files DocFilePair[]）
 * - PUT /order/{id}/complete            链尾节点确认完成
 * - 子步骤：NodeSubstepPanel 内部处理
 */
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Snackbar,
  Alert,
} from '@mui/material';
import { useCallback, useEffect, useState, useMemo } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { getAdvancePermission, advanceOrder, completeOrder } from '@/api/fulfillment';
import { getNodeDocRules, type NodeDocRuleItem } from '@/api/substep';
import { useNodeConfig } from '@/utils/nodeConfig';
import NodeSubstepPanel from '@/components/mobile/NodeSubstepPanel';
import { MobileAppBar, NodeHero, InfoCard } from '@/components/mobile/ui';
import { T } from '@/theme/tokens';
import NodeDocUploader from '@/components/mobile/NodeDocUploader';
import CreateContainerDialog from '@/components/mobile/CreateContainerDialog';
import NodeDetailCard from '@/components/mobile/NodeDetailCard';
import type { NodeActionPermissionVO, DocFilePair, NodeActionHintVO, ContainerArchiveFormItem } from '@/api/types';

export default function NodeTaskPage() {
  const { orderId = '' } = useParams();
  const location = useLocation();
  const passedState = (location.state as {
    nodeCode?: string;
    orderNo?: string;
  } | null);
  const navigate = useNavigate();
  const { getNodeName } = useNodeConfig();

  const [loading, setLoading] = useState(true);
  const [permission, setPermission] = useState<NodeActionPermissionVO | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [snack, setSnack] = useState<{ msg: string; sev: 'success' | 'error' | 'info' } | null>(null);

  // 建柜弹窗（由 NodeSubstepPanel.needArchiveCreate 入口驱动，onRequestNewArchive 回调打开）
  const [createContainerOpen, setCreateContainerOpen] = useState(false);
  const [createContainerHint, setCreateContainerHint] = useState<NodeActionHintVO | null>(null);

  // 节点级附件规则与已上传文件（ advance.files 来源）
  const [nodeRules, setNodeRules] = useState<NodeDocRuleItem[]>([]);
  const [nodeFiles, setNodeFiles] = useState<DocFilePair[]>([]);

  // nodeCode 优先取任务卡片透传，无则以后端 permission.currentNodeCode 兜底（支持刷新/外部入口）
  const [effectiveNodeCode, setEffectiveNodeCode] = useState<string | undefined>(passedState?.nodeCode);
  const displayOrderNo = passedState?.orderNo || orderId;

  useEffect(() => {
    if (!effectiveNodeCode && permission?.currentNodeCode) {
      setEffectiveNodeCode(permission.currentNodeCode);
    }
  }, [effectiveNodeCode, permission?.currentNodeCode]);

  const loadPermission = useCallback(async () => {
    if (!orderId) return;
    setLoading(true);
    try {
      const perm = await getAdvancePermission(orderId);
      setPermission(perm);
    } catch (e: unknown) {
      setSnack({ msg: (e as { message?: string })?.message || '加载失败', sev: 'error' });
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => {
    loadPermission();
  }, [loadPermission]);

  // 加载当前节点级附件规则（对齐后台 UpdateNodeDialog：展示当前节点全部 rules，不再过滤 substepCode）
  useEffect(() => {
    const nodeCode = effectiveNodeCode || permission?.currentNodeCode;
    if (!nodeCode) return;
    let cancelled = false;
    getNodeDocRules(nodeCode, orderId)
      .then((rules) => {
        if (cancelled) return;
        setNodeRules(Array.isArray(rules) ? rules : []);
      })
      .catch((err) => {
        console.error('加载节点附件规则失败', err);
      });
    return () => {
      cancelled = true;
    };
  }, [effectiveNodeCode, permission?.currentNodeCode, orderId]);

  const allRequiredDocsUploaded = useMemo(
    () => nodeRules.filter((r) => r.required).every((r) => nodeFiles.some((f) => f.docType === r.docType)),
    [nodeRules, nodeFiles],
  );

  const handleAdvance = async () => {
    if (!permission || !orderId) return;
    if (nodeRules.length > 0 && !allRequiredDocsUploaded) {
      setSnack({ msg: '请先完成节点必填附件上传', sev: 'error' });
      return;
    }
    setSubmitting(true);
    try {
      if (permission.lastNode) {
        await completeOrder(orderId);
        setSnack({ msg: '订单已完成', sev: 'success' });
        setTimeout(() => navigate('/mobile/tasks', { replace: true }), 1200);
        return;
      }
      const result = await advanceOrder(orderId, {
        expectedFromNodeCode: permission.currentNodeCode,
        trigger: 'MOBILE',
        files: nodeFiles,
      });
      // 动作提示解析（建柜已改由 load 节点 onRequestNewArchive 入口驱动）
      const resObj =
        result && typeof result === 'object'
          ? (result as { code?: string; message?: string; currentNodeCode?: string })
          : null;
      // 幂等成功（手动推进与船期自动流转撞车的后到者）：info 提示 + 刷新收尾
      if (resObj?.code === 'IDEMPOTENT') {
        setSnack({ msg: resObj.message || '该节点已推进，已为你刷新最新进度', sev: 'info' });
        await loadPermission();
        return;
      }
      setSnack({
        msg: permission.advanceMode === 'OBSERVE_CONFIRM' ? '已确认完成' : '已提交推进',
        sev: 'success',
      });
      const after = await getAdvancePermission(orderId);
      if (after.currentNodeCode !== permission.currentNodeCode) {
        setSnack({ msg: `已推进至 ${after.nextNodeCode ?? after.currentNodeCode}`, sev: 'success' });
        setTimeout(() => navigate('/mobile/tasks', { replace: true }), 1200);
      } else {
        setPermission(after);
      }
    } catch (e: unknown) {
      setSnack({ msg: (e as { message?: string })?.message || '推进失败', sev: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  const isObserve = permission?.advanceMode === 'OBSERVE_CONFIRM';
  const isReadOnly = permission != null && !permission.canAdvance;
  const btnLabel = isObserve ? '确认完成' : '提交推进';
  const btnVariant = isObserve ? 'outlined' : 'contained';

  return (
    <Box sx={{ pb: 2 }}>
      <MobileAppBar title="节点作业" onBack={() => navigate('/mobile/tasks', { replace: true })} />

      <Box sx={{ px: 2, pt: 2 }}>
        <NodeHero
          orderNo={displayOrderNo}
          nodeName={getNodeName(permission?.currentNodeCode)}
          lastNode={permission?.lastNode}
        />

        {permission?.observeNode && (
          <InfoCard>
            本节点为观察节点，录入船期后将自动推进
            {permission.autoAdvanceEnabled ? `（${permission.autoSourceLabel}）` : ''}，也可手动确认完成。
            {permission.lastNode && '本节点为订单最终节点，确认完成后订单即完结。'}
          </InfoCard>
        )}

        {isReadOnly && (
          <Box>
            <InfoCard>
              本节点由{permission?.ownerRoleNames?.length
                ? `【${permission.ownerRoleNames.join('、')}】`
                : '责任人'}负责处理，以下为节点详情（只读）。
            </InfoCard>
            <NodeDetailCard orderId={orderId} nodeCode={effectiveNodeCode || permission?.currentNodeCode || ''} />
          </Box>
        )}

        {/* 节点级附件上传（对齐后台 UpdateNodeDialog） */}
        {!isReadOnly && effectiveNodeCode && (
          <NodeDocUploader
            orderId={orderId}
            nodeCode={effectiveNodeCode}
            rules={nodeRules}
            onChange={setNodeFiles}
          />
        )}

        {/* 子步骤面板（对齐后台 NodeSubstepPanel） */}
        {!isReadOnly && effectiveNodeCode && (
          <Box sx={{ mt: 2 }}>
            <NodeSubstepPanel
              orderId={orderId}
              nodeCode={effectiveNodeCode}
              onComplete={() => loadPermission()}
              onRequestNewArchive={(hint) => {
                setCreateContainerHint(hint || null);
                setCreateContainerOpen(true);
              }}
            />
          </Box>
        )}

        {/* 推进按钮 */}
        {!isReadOnly && (
          <Button
            fullWidth
            variant={btnVariant}
            disabled={!permission?.canAdvance || submitting}
            onClick={handleAdvance}
            sx={{ mt: 2.5, height: 50, fontSize: '16px', fontWeight: 700, letterSpacing: '.04em', borderRadius: '14px' }}
          >
            {submitting ? <CircularProgress size={20} sx={{ color: 'inherit' }} /> : btnLabel}
          </Button>
        )}
        {isReadOnly && (
          <Typography sx={{ textAlign: 'center', fontSize: '12px', color: T.ink3, mt: 2.25, mb: 1, lineHeight: 1.6 }}>
            如需操作本节点，请联系{permission?.ownerRoleNames?.length
              ? `【${permission.ownerRoleNames.join('、')}】`
              : '责任人'}处理
          </Typography>
        )}
      </Box>

      <Snackbar
        open={!!snack}
        autoHideDuration={3000}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        onClose={() => setSnack(null)}
      >
        <Alert severity={snack?.sev} sx={{ fontSize: '13px' }}>
          {snack?.msg}
        </Alert>
      </Snackbar>

      {/* 建立柜档案弹窗（由节点建档 needArchiveCreate 入口 / onRequestNewArchive 驱动） */}
      <CreateContainerDialog
        open={createContainerOpen}
        orderId={orderId}
        prefillContainerNo={createContainerHint?.prefillContainerNo}
        onClose={() => setCreateContainerOpen(false)}
        onCreated={async () => {
          setCreateContainerOpen(false);
          setSnack({ msg: '柜档案已创建，正在提交推进', sev: 'success' });
          // 重新走推进（建柜后 advance 通过 ≥1 柜档案校验，进入 load 节点）
          await handleAdvance();
        }}
      />
    </Box>
  );
}
